import React, { FC, useEffect, useRef, useState } from 'react'

import { DatasetTables } from './DatasetTables'
import { useDatasets } from '../hooks/useDatasets'
import { alertError, alertSuccess } from 'api'
import { DatasetRelationships } from './DatasetRelationships'
import * as S from './DescriptionDatasets.style'
import { DatasetInfo } from './DatasetInfo'
import { DatasetTabs } from './DatasetTabs/DatasetTabs'

export const DescriptionDatasets: FC = () => {
  const { data, refetch, updateDataset, error } = useDatasets()

  const [selectedDatasetIndex, setSelectedDatasetIndex] = useState<number>(null)
  const [selectedDatasetSubtabIndex, setSelectedDatasetSubtabIndex] = useState<number>(0)
  const [versionSelections, setVersionSelections] = useState<Record<number, any>>({})
  const [openedDropdownIndex, setOpenedDropdownIndex] = useState<number | null>(null)

  const [lastEditedDatasetId, setLastEditedDatasetId] = useState<string | null>(null)

  const selectedVersionData = versionSelections[selectedDatasetIndex] || {}
  const selectedDatasetId = selectedVersionData?.datasetId

  useEffect(() => {
    if (selectedDatasetIndex === null && data?.length > 0) {
      setSelectedDatasetIndex(0)
    }

    if (data?.length) {
      const updatedSelections: Record<number, any> = {}
      let editedIndex: number | null = null

      data.forEach((dataset, idx) => {
        const fallbackVersion = dataset.activeVersionDescription || dataset.allDatasetVersions[0]

        if (fallbackVersion) {
          const hasVersion = dataset.allDatasetVersions.find(v => v.datasetId === fallbackVersion.datasetId)
          updatedSelections[idx] = {
            ...fallbackVersion,
            datasetName: dataset.datasetName,
            isActiveVersion: hasVersion.isActiveVersion,
          }
        }

        if (lastEditedDatasetId) {
          const match = dataset.allDatasetVersions.find(v => v.datasetId === lastEditedDatasetId)
          if (match) {
            updatedSelections[idx] = {
              ...match,
              datasetName: dataset.datasetName,
              isActiveVersion: match.isActiveVersion,
            }
            editedIndex = idx
          }
        }
      })

      setVersionSelections(updatedSelections)

      if (editedIndex !== null) {
        setSelectedDatasetIndex(editedIndex)
        setLastEditedDatasetId(null)
      } else if (selectedDatasetIndex === null) {
        setSelectedDatasetIndex(0)
      }
    }
  }, [data])

  const handleVersionChange = (datasetIdx: number, versionId: string) => {
    const dataset = data?.[datasetIdx]
    const selectedVersion = dataset?.allDatasetVersions.find(v => v.datasetId === versionId)
    if (selectedVersion) {
      setVersionSelections(prev => ({
        ...prev,
        [datasetIdx]: {
          ...selectedVersion,
          datasetName: dataset.datasetName,
        },
      }))
    }
    setOpenedDropdownIndex(null)
  }

  const handleUpdateDataset = async (
    description: string,
    mandatoryDataPoints: string,
    isActive: boolean,
    powerBiId?: string,
  ) => {
    const { datasetId, datasetVersion } = selectedVersionData
    const params = {
      datasetDescription: description,
      datasetMandatoryDataPoints: mandatoryDataPoints,
      datasetActivate: isActive,
      datasetVersion: datasetVersion,
      powerBiId: powerBiId,
    }

    try {
      await updateDataset(datasetId, params)
      alertSuccess('Dataset configuration has been updated', 3000)
      setLastEditedDatasetId(datasetId)
      await refetch()
    } catch (error) {
      switch (error.response.status) {
        case 412:
          throw alertError('Error! Someone already made changes. Please reload page and try again.')
      }
      alertError('Sorry but something is going wrong please ping support!')
    }
  }

  const dropdownRefs = useRef<Record<number, HTMLDivElement | null>>({})

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const currentRef = dropdownRefs.current[openedDropdownIndex ?? -1]
      if (currentRef && !currentRef.contains(event.target as Node)) {
        setOpenedDropdownIndex(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openedDropdownIndex])

  if (error && !data?.length) return null

  return (
    <div>
      <DatasetTabs
        data={data}
        selectedDatasetIndex={selectedDatasetIndex}
        onSelectDataset={setSelectedDatasetIndex}
        versionSelections={versionSelections}
        onVersionChange={handleVersionChange}
      />

      <DatasetInfo
        datasetId={selectedDatasetId}
        updatedAt={selectedVersionData?.updatedAt}
        createdBy={selectedVersionData?.createdBy}
        updatedBy={selectedVersionData?.updatedBy}
        name={selectedVersionData?.datasetName}
        type={selectedVersionData?.datasetType}
        powerBiId={selectedVersionData?.powerBiId}
        isActiveVersion={selectedVersionData?.isActiveVersion}
        versionLabel={selectedVersionData?.datasetVersionLabel}
        version={selectedVersionData?.datasetVersion}
        description={selectedVersionData?.datasetDescription}
        mandatoryDataPoints={selectedVersionData?.datasetMandatoryDataPoints}
        allDatasets={data}
        onChange={handleUpdateDataset}
        refetch={refetch}
      />

      <S.SubtabWrapper>
        {['Tables', 'Relationships'].map((label, index) => (
          <S.Subtab
            key={label}
            active={selectedDatasetSubtabIndex === index}
            onClick={() => setSelectedDatasetSubtabIndex(index)}>
            {label}
          </S.Subtab>
        ))}
      </S.SubtabWrapper>

      {selectedDatasetSubtabIndex === 0 && <DatasetTables datasetId={selectedDatasetId} />}
      {selectedDatasetSubtabIndex === 1 && <DatasetRelationships datasetId={selectedDatasetId} />}
    </div>
  )
}
